import { useCallback, useEffect, useRef, type MouseEvent } from 'react';
import { DOUBLE_CLICK_TIMEOUT } from './constants';
import type { RightDrawerTableItemType } from './types';

type UseRowClickIntentArgs = {
  getRowAction: (item: RightDrawerTableItemType) => RightDrawerTableItemType;
  onRowClick: (item: RightDrawerTableItemType) => Promise<void>;
  onRowDoubleClick: (item: RightDrawerTableItemType) => Promise<void>;
};

export const useRowClickIntent = ({
  getRowAction,
  onRowClick,
  onRowDoubleClick,
}: UseRowClickIntentArgs) => {
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
    },
    []
  );

  return useCallback(
    (event: MouseEvent, item: RightDrawerTableItemType) => {
      event.preventDefault();
      const options = getRowAction(item);

      if (event.detail === 1) {
        clickTimeoutRef.current = setTimeout(() => {
          void onRowClick(options);
        }, DOUBLE_CLICK_TIMEOUT);
        return;
      }

      if (event.detail === 2) {
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
        void onRowDoubleClick(options);
      }
    },
    [getRowAction, onRowClick, onRowDoubleClick]
  );
};
